import { gql } from "@apollo/client";
import { ESTABLISHMENT_FIELDS_FRAGMENT } from "../../../common/model/fieldsFragment";
import { ESTABLISHMENT_STATUS } from "../../../common/model/enum";

// Taille de page de la table /admin/establishments (pagination côté serveur).
export const ESTABLISHMENTS_PAGE_SIZE = 10;

// En dessous de ce nombre de caractères, la recherche n'est pas envoyée au serveur.
export const ESTABLISHMENT_SEARCH_MIN_LENGTH = 3;

// Valeurs acceptées par QR_LIST_ESTABLISHMENT (EstablishmentStatusEnum du
// controller). Pas de nouveau statut côté front.
export const ESTABLISHMENT_STATUS_FILTERS = [
  ESTABLISHMENT_STATUS.ACTIVATED,
  ESTABLISHMENT_STATUS.MISSING_INFORMATIONS,
  ESTABLISHMENT_STATUS.WAITING_FOR_APPROVAL,
  ESTABLISHMENT_STATUS.DISABLED,
];

/** Terme de recherche tel qu'envoyé au serveur, ou undefined sous le seuil. */
export const normalizeEstablishmentSearch = (value) => {
  const trimmed = (value ?? "").trim();
  return trimmed.length >= ESTABLISHMENT_SEARCH_MIN_LENGTH ? trimmed : undefined;
};

/**
 * Variables de GET_ESTABLISHMENTS_PAGE : seul point de construction des
 * paramètres de la liste. `search` et `status` n'existent dans l'objet que
 * lorsqu'ils doivent être envoyés (pas de clé à `undefined`).
 */
export const buildEstablishmentsPageVariables = ({
  organizationId,
  currentPage = 0,
  search,
  status,
}) => {
  const variables = {
    organizationId,
    paginationParams: { currentPage, sizePage: ESTABLISHMENTS_PAGE_SIZE },
  };
  const normalizedSearch = normalizeEstablishmentSearch(search);
  if (normalizedSearch) variables.search = normalizedSearch;
  if (ESTABLISHMENT_STATUS_FILTERS.includes(status)) variables.status = status;
  return variables;
};

// Une page de la table /admin/establishments (pagination et filtres côté
// serveur). La liste centrale d'AdminProvider (GET_ADMIN_ESTABLISHMENTS, 1000
// éléments) n'est volontairement pas utilisée ici.
export const GET_ESTABLISHMENTS_PAGE = gql`
  query GetEstablishmentsPage(
    $paginationParams: BaseCollection!
    $organizationId: String!
    $search: String
    $status: EstablishmentStatusEnum
  ) {
    QR_LIST_ESTABLISHMENT(
      paginationParams: $paginationParams
      organizationId: $organizationId
      search: $search
      status: $status
    ) {
      code
      error {
        message
        timestamp
        correlationId
      }
      data {
        totalElements
        totalPages
        currentPage
        content {
          id
          status
          activities
          userRole
          isOwner
          company {
            legalName
          }
        }
      }
    }
  }
`;

export const GET_ESTABLISHMENT = gql`
  query GetEstablishment($establishmentId: String!) {
    QR_GET_ESTABLISHMENT(establishmentId: $establishmentId) {
      code
      data { ...EstablishmentFields }
    }
  }
  ${ESTABLISHMENT_FIELDS_FRAGMENT}
`;

export const CREATE_ESTABLISHMENT_MUTATION = gql`
  mutation CreateEstablishment($establishmentInput: EstablishmentInput!) {
    MT_ADD_ESTABLISHMENT(establishmentInput: $establishmentInput) {
      code
      data { ...EstablishmentFields }
    }
  }
  ${ESTABLISHMENT_FIELDS_FRAGMENT}
`;

export const UPDATE_ESTABLISHMENT_MUTATION = gql`
  mutation UpdateEstablishment($establishmentId: String!, $establishmentInput: EstablishmentInput!) {
    MT_UPDATE_ESTABLISHMENT(establishmentId: $establishmentId, establishmentInput: $establishmentInput) {
      code
      data { ...EstablishmentFields }
    }
  }
  ${ESTABLISHMENT_FIELDS_FRAGMENT}
`;

export const DELETE_ESTABLISHMENT_MUTATION = gql`
  mutation DeleteEstablishment(
    $establishmentId: String!
  ) {
    MT_DELETE_ESTABLISHMENT(
      establishmentId: $establishmentId
    ) {
      code
      error {
        message
        timestamp
        correlationId
      }
    }
  }
`;
